import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Search, Download, Mail, ChevronRight, AlertTriangle, MapPin, Clock } from "lucide-react";

const STATUS_CHIPS = [
  { key: "active", label: "Active" },
  { key: "onboarding_incomplete", label: "Awaiting onboarding completion" },
  { key: "pending_review", label: "Pending approval" },
  { key: "all", label: "All" },
];
const TYPES = [
  { v: "", l: "All types" },
  { v: "process_server", l: "Process server" },
  { v: "notary", l: "Notary" },
];
const TYPE_LABEL = { process_server: "Process server", notary: "Notary", both: "Both" };
const PA_STATUS = {
  new_v1_accepted: { label: "Accepted", cls: "bg-emerald-50 text-emerald-700" },
  legacy_grandfathered: { label: "Grandfathered", cls: "bg-slate-100 text-slate-600" },
  not_accepted: { label: "Not accepted", cls: "bg-amber-50 text-amber-700" },
};
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : "—");

export const ProviderRoster = () => {
  const nav = useNavigate();
  const [status, setStatus] = useState("active");
  const [selStates, setSelStates] = useState([]);
  const [selCounties, setSelCounties] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [data, setData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [paOnly, setPaOnly] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/provider-roster", {
        params: { status, states: selStates.join(","), counties: selCounties.join(","), q: q.trim(), type },
      });
      setData(data);
    } catch {
      toast.error("Could not load the provider roster");
    }
  }, [status, selStates, selCounties, q, type]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const toggleState = (s) =>
    setSelStates((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  const toggleCounty = (f) =>
    setSelCounties((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));

  const doExport = () => {
    const p = new URLSearchParams({ status, states: selStates.join(","), counties: selCounties.join(","), q: q.trim(), type });
    window.open(`${API}/admin/provider-roster/export?${p.toString()}`, "_blank", "noopener");
  };

  const sendReminder = async (providerId) => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/admin/provider-roster/remind", { provider_id: providerId });
      toast.success(res.sent ? "Reminder sent" : "Skipped (reminded within 72h)");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send reminder");
    } finally {
      setBusy(false);
    }
  };

  const sendBulk = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/admin/provider-roster/remind", {
        status: "onboarding_incomplete", states: selStates.join(","), counties: selCounties.join(","), q: q.trim(), type,
      });
      toast.success(`Sent ${res.sent} reminder${res.sent === 1 ? "" : "s"}${res.skipped ? `, skipped ${res.skipped}` : ""}`);
      setConfirmOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk reminder failed");
    } finally {
      setBusy(false);
    }
  };

  const rows = data?.rows || [];
  const displayRows = paOnly ? rows.filter((r) => r.pa_status === "not_accepted") : rows;
  const counts = data?.status_counts || {};
  const stateCounts = data?.state_counts || {};
  const eligibleCount = data?.reminder_eligible_count || 0;
  const paNotAccepted = data?.pa_not_accepted_count || 0;
  const stateSummary = useMemo(
    () => Object.entries(stateCounts).map(([s, n]) => `${s} ${n}`).join(", "),
    [stateCounts]
  );

  return (
    <div className="space-y-5" data-testid="provider-roster">
      {/* Status chips */}
      <div className="flex flex-wrap gap-2" data-testid="roster-status-chips">
        {STATUS_CHIPS.map((c) => (
          <button
            key={c.key}
            data-testid={`roster-chip-${c.key}`}
            onClick={() => setStatus(c.key)}
            className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition ${status === c.key ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
          >
            {c.label} <span className="ml-1 opacity-70">{counts[c.key] ?? 0}</span>
          </button>
        ))}
      </div>

      {/* ARPS Provider Service Agreement — outstanding acceptances */}
      {paNotAccepted > 0 && (
        <div data-testid="roster-pa-summary" className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            <b data-testid="roster-pa-not-accepted-count">{paNotAccepted}</b> ARPS-track provider{paNotAccepted === 1 ? "" : "s"} {paNotAccepted === 1 ? "has" : "have"} not accepted the <b>ARPS Provider Service Agreement</b>
            {(selStates.length > 0 || selCounties.length > 0 || type || q.trim()) ? " (current filters)" : ""}.
          </span>
          {!paOnly ? (
            <button data-testid="roster-pa-filter-btn" onClick={() => { setStatus("all"); setPaOnly(true); }} className="ml-auto rounded-full bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-700">
              View not accepted
            </button>
          ) : (
            <button data-testid="roster-pa-filter-clear" onClick={() => setPaOnly(false)} className="ml-auto rounded-full bg-white px-3 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-300 hover:bg-amber-100">
              Clear "Not accepted" filter
            </button>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="roster-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="pl-9" />
        </div>
        <select data-testid="roster-type" value={type} onChange={(e) => setType(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm">
          {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
        </select>
        <Button variant="outline" size="sm" data-testid="roster-export" onClick={doExport}><Download className="mr-1.5 h-4 w-4" />Export CSV</Button>
        {status === "onboarding_incomplete" && (
          <Button size="sm" data-testid="roster-bulk-remind" disabled={eligibleCount === 0} onClick={() => setConfirmOpen(true)} className="bg-indigo-600 text-white hover:bg-indigo-700">
            <Mail className="mr-1.5 h-4 w-4" />Send reminder to all incomplete ({eligibleCount})
          </Button>
        )}
      </div>

      {/* State multi-select */}
      {(data?.state_options || []).length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="roster-state-filter">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <MapPin className="h-3.5 w-3.5" /> States served
            {selStates.length > 0 && <button data-testid="roster-state-clear" onClick={() => setSelStates([])} className="ml-auto text-xs font-medium text-indigo-600">Clear</button>}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(data.state_options || []).map((s) => (
              <button
                key={s}
                data-testid={`roster-state-${s}`}
                onClick={() => toggleState(s)}
                className={`rounded-full px-2.5 py-1 text-xs font-semibold transition ${selStates.includes(s) ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
              >
                {s}{stateCounts[s] != null ? ` ${stateCounts[s]}` : ""}
              </button>
            ))}
          </div>
          {stateSummary && <p className="mt-2 text-xs text-slate-400" data-testid="roster-state-summary">{stateSummary}</p>}
        </div>
      )}

      {/* County multi-select — shown only when county data exists for the current selection */}
      {(data?.county_options || []).length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="roster-county-filter">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <MapPin className="h-3.5 w-3.5" /> Service counties
            {selCounties.length > 0 && <button data-testid="roster-county-clear" onClick={() => setSelCounties([])} className="ml-auto text-xs font-medium text-indigo-600">Clear</button>}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(data.county_options || []).map((c) => (
              <button
                key={c.fips}
                data-testid={`roster-county-${c.fips}`}
                onClick={() => toggleCounty(c.fips)}
                className={`rounded-full px-2.5 py-1 text-xs font-semibold transition ${selCounties.includes(c.fips) ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Rows */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {displayRows.length === 0 && <div className="p-10 text-center text-sm text-slate-400" data-testid="roster-empty">No providers match these filters.</div>}
        <div className="divide-y divide-slate-100">
          {displayRows.map((r) => (
            <div key={r.id} data-testid={`roster-row-${r.id}`} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-slate-900">{r.name || "(no name)"}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{TYPE_LABEL[r.type] || r.type}</span>
                  {r.pa_required && PA_STATUS[r.pa_status] && (
                    <span data-testid={`roster-pa-status-${r.id}`} title="ARPS Provider Service Agreement" className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${PA_STATUS[r.pa_status].cls}`}>Agreement: {PA_STATUS[r.pa_status].label}</span>
                  )}
                  {r.override && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Override</span>}
                  {r.opted_out_reminders && <span data-testid={`roster-optout-${r.id}`} className="rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-600">Opted out of reminders</span>}
                  {r.class === "onboarding_incomplete" && r.days_stalled != null && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-600"><Clock className="h-3 w-3" />{r.days_stalled}d stalled</span>
                  )}
                </div>
                <div className="text-sm text-slate-500">{r.email}</div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                  <span><b className="text-slate-500">States:</b> {r.states.join(", ") || "—"}</span>
                  <span><b className="text-slate-500">Counties:</b> {r.counties.length ? `${r.counties.slice(0, 3).join(", ")}${r.counties.length > 3 ? ` +${r.counties.length - 3}` : ""}` : "—"}</span>
                  <span><b className="text-slate-500">Signup:</b> {fmtDate(r.signup_date)}</span>
                  {r.class === "active" ? <span><b className="text-slate-500">Approved:</b> {fmtDate(r.approval_date)}</span> : null}
                </div>
                {r.class !== "active" && (
                  <div className="mt-1.5 text-xs">
                    <span className="font-semibold text-slate-600" data-testid={`roster-progress-${r.id}`}>{r.progress_done} of {r.progress_total} done</span>
                    {r.missing.length > 0 && (
                      <span className="ml-2 inline-flex flex-wrap items-center gap-1 text-amber-700">
                        <AlertTriangle className="h-3 w-3" />
                        {r.missing.slice(0, 4).join(", ")}{r.missing.length > 4 ? "…" : ""}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex shrink-0 gap-2">
                {r.class === "onboarding_incomplete" && (
                  <Button size="sm" variant="outline" data-testid={`roster-remind-${r.id}`} disabled={busy || !r.reminder_eligible} onClick={() => sendReminder(r.id)}>
                    <Mail className="mr-1 h-3.5 w-3.5" />{r.reminder_eligible ? "Send reminder" : "Reminded"}
                  </Button>
                )}
                <Button size="sm" variant="outline" data-testid={`roster-view-${r.id}`} onClick={() => nav(`/admin/servers/${r.id}`)}>
                  View <ChevronRight className="ml-0.5 h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="roster-bulk-confirm">
          <DialogHeader><DialogTitle>Send onboarding reminders</DialogTitle>
            <DialogDescription>Emails incomplete providers their own missing items; anyone reminded in the last 72 hours is skipped.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            This will email <b data-testid="bulk-will-email">{eligibleCount}</b> incomplete provider{eligibleCount === 1 ? "" : "s"}
            {selStates.length > 0 ? ` in ${selStates.join(", ")}` : ""}, each listing their own missing items.
          </p>
          <ul className="mt-1 space-y-1 text-xs text-slate-500">
            <li><b className="text-emerald-600" data-testid="bulk-count-email">{eligibleCount}</b> will be emailed</li>
            <li><b className="text-amber-600" data-testid="bulk-count-72h">{data?.skipped_72h ?? 0}</b> skipped (reminded in last 72h)</li>
            <li><b className="text-slate-500" data-testid="bulk-count-optout">{data?.skipped_opted_out ?? 0}</b> skipped (opted out)</li>
          </ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button data-testid="roster-bulk-confirm-send" disabled={busy || eligibleCount === 0} onClick={sendBulk} className="bg-indigo-600 text-white hover:bg-indigo-700">Send {eligibleCount} reminder{eligibleCount === 1 ? "" : "s"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
